import { initUrqlClient, SSRData, withUrqlClient } from 'next-urql';
import { Client, defaultExchanges, ssrExchange } from 'urql';
import { operationNameExchange } from './operationNameExchange';
import { HandledError, handleErrorForSSR } from '~/types/handled-error';
import { medicoUserAuthStorage } from '~/utils/storage/ubieAppStorage';

/**
 * Orthrus accessToken を medinavi-backend consumer GraphQL へ渡す。
 * `_app` の medinavi urql クライアントと同型。withUrqlClient は内側 Provider を作るため、
 * ここに付けないと OTP mutation 等が Authorization なしで飛ぶ。
 */
const medinaviBackendFetchOptions = (): RequestInit => {
  const auth = medicoUserAuthStorage.get();
  // `ubie-auth` は inquiry 側の ubieAuthStorage と共有しており authSession を持たない形状もありうる
  const accessToken = auth?.authSession?.accessToken;
  if (accessToken == null) return {};
  return {
    headers: {
      'X-Ubie-Authorization': `Bearer ${accessToken}`,
    },
  };
};

export function initUrqlMedinaviBackendClientForSSR(): {
  client: Client;
  extractCacheData: () => SSRData;
} {
  const ssrCache = ssrExchange({ isClient: false });
  const client = initUrqlClient(
    {
      url: process.env.NEXT_PUBLIC_MEDINAVI_BACKEND_URL as string,
      exchanges: [operationNameExchange, ssrCache, ...defaultExchanges],
    },
    false,
  );
  if (client == null) {
    const err = new Error('Failed to init urql client');
    handleErrorForSSR(
      new HandledError(err, {
        shouldSendToSentry: true,
        requireReStart: false,
      }),
    );
    throw err;
  }

  return { client, extractCacheData: () => ssrCache.extractData() };
}

export const withUrqlMedinaviBackendClientForSSR = withUrqlClient(
  () => ({
    url: process.env.NEXT_PUBLIC_MEDINAVI_BACKEND_URL as string,
    fetchOptions: medinaviBackendFetchOptions,
  }),
  { ssr: false },
);
